import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingCart, Tag, Bookmark, Truck, Check } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/cart/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Estimated delivery: ships next business day, then USPS Priority 1–3 business days
const deliveryEstimate = () => {
  const addBiz = (d, n) => {
    const out = new Date(d);
    while (n > 0) {
      out.setDate(out.getDate() + 1);
      if (out.getDay() !== 0 && out.getDay() !== 6) n -= 1;
    }
    return out;
  };
  const ship = addBiz(new Date(), 1);
  const lo = addBiz(ship, 1);
  const hi = addBiz(ship, 3);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(lo)} – ${fmt(hi)}`;
};

const ProgressBar = ({ pct, color, label, testid }) => (
  <div data-testid={testid} className="bg-[#0a0a0c] border border-[#22242a] p-3">
    <div className="h-1.5 bg-[#22242a] mb-2 overflow-hidden">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
    <p className="f-mono text-[10px] uppercase tracking-wider" style={{ color }}>{label}</p>
  </div>
);

export default function CartPage() {
  const { items, savedItems, setQty, removeItem, saveForLater, moveToCart, removeSaved, totalCents, count } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, percent_off }
  const [confirmRemove, setConfirmRemove] = useState(null); // cart_key pending confirm
  const navigate = useNavigate();

  // Owner's rule: 3+ items store-wide → 10% off (server re-applies at checkout)
  const discountCents = count >= 3 ? Math.round(totalCents * 0.1) : 0;
  const hasPhysical = items.some((i) => !i.is_digital);
  const afterDiscount = totalCents - discountCents;
  const shippingCents = hasPhysical && afterDiscount < 10000 ? 999 : 0;
  const payableCents = afterDiscount + shippingCents;
  const shipPct = Math.min(100, (afterDiscount / 10000) * 100);
  const bulkPct = Math.min(100, (count / 3) * 100);

  const savedSection = savedItems.length > 0 && (
    <div className="pt-6" data-testid="saved-for-later">
      <h2 className="f-disp text-xl uppercase text-white mb-3 flex items-center gap-2">
        <Bookmark size={16} className="text-[#f5b700]" /> Saved For Later
      </h2>
      <div className="space-y-3">
        {savedItems.map((i) => (
          <div key={i.cart_key} data-testid={`saved-item-${i.cart_key}`} className="bg-[#121316] border border-[#22242a] p-4 flex flex-wrap gap-4 items-center">
            <img src={i.image} alt={i.name} className="w-16 h-16 object-cover shrink-0 bg-[#1a1c22]" />
            <div className="flex-1 min-w-0">
              <h3 className="f-disp text-base uppercase text-white line-clamp-2">{i.name}</h3>
              <p className="f-mono text-sm text-[#39ff14] tabular-nums">${(i.price_cents / 100).toFixed(2)}</p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                data-testid={`move-to-cart-${i.cart_key}`}
                onClick={() => moveToCart(i.cart_key)}
                className="f-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors"
              >
                Move to Cart
              </button>
              <button
                data-testid={`saved-remove-${i.cart_key}`}
                onClick={() => removeSaved(i.cart_key)}
                className="w-8 h-8 text-[#8e929b] hover:text-red-500 flex items-center justify-center transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    try {
      const { data } = await axios.get(`${API}/coupons/validate`, { params: { code } });
      setCoupon(data);
      toast.success(`${data.code} applied — ${data.percent_off}% off at checkout`);
    } catch {
      setCoupon(null);
      toast.error("That code isn't valid");
    }
  };

  const checkout = async () => {
    if (!items.length || checkingOut) return;
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
        origin_url: window.location.origin,
        coupon_code: coupon ? coupon.code : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed", {
        description: e.response?.data?.detail || "Could not start Stripe checkout. Try again.",
      });
      setCheckingOut(false);
    }
  };

  return (
    <div className="bg-[#0a0a0c] text-[#ecebe4] min-h-screen selection:bg-[#39ff14] selection:text-black">
      <nav className="sticky top-0 z-50 bg-[#0a0a0c]/90 backdrop-blur border-b border-[#22242a] px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="cart-back-link" className="flex items-center gap-2 f-mono text-xs uppercase tracking-[0.14em] text-[#8e929b] hover:text-[#39ff14] transition-colors">
          <ArrowLeft size={14} /> Back to Catalog
        </Link>
        <span className="flex items-center gap-3">
          <img src="/logo.png" alt="The Print Plug logo" className="w-10 h-10 object-contain" />
          <span className="f-disp tracking-wider text-xl uppercase leading-none text-white">
            YOUR STACK<span className="text-[#39ff14]">.</span>
          </span>
        </span>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <h1 className="f-disp text-[clamp(32px,4vw,56px)] leading-[0.95] tracking-[-0.03em] uppercase text-white mb-10">
          Review Stack
        </h1>

        {items.length === 0 ? (
          <>
            <div data-testid="cart-empty-state" className="bg-[#121316] border border-[#22242a] p-12 text-center space-y-6">
              <ShoppingCart size={32} className="mx-auto text-[#8e929b]" />
              <p className="f-body text-sm text-[#8e929b]">Your stack is empty. Load it up with prop bundles.</p>
              <button
                onClick={() => navigate("/")}
                data-testid="cart-empty-shop-button"
              className="f-mono text-xs uppercase tracking-[0.14em] px-8 py-4 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors d1-glow"
            >
              Shop Prop Money
            </button>
          </div>
            {savedSection}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-8 space-y-4" data-testid="cart-items-list">
              {items.map((i) => (
                <div key={i.cart_key} data-testid={`cart-item-${i.cart_key}`} className="bg-[#121316] border border-[#22242a] p-4 flex flex-wrap gap-4 items-center">
                  <img src={i.image} alt={i.name} className="w-16 h-16 sm:w-20 sm:h-20 object-cover shrink-0 bg-[#1a1c22]" />
                  <div className="flex-1 min-w-0 basis-[calc(100%-80px)] sm:basis-auto">
                    <h3 className="f-disp text-lg uppercase text-white line-clamp-2">{i.name}</h3>
                    <p className="f-mono text-sm text-[#39ff14] tabular-nums">${(i.price_cents / 100).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center justify-between w-full sm:w-auto sm:justify-start gap-2">
                    <div className="flex items-center gap-2 f-mono">
                      <button
                        data-testid={`qty-minus-${i.cart_key}`}
                        onClick={() => setQty(i.cart_key, i.qty - 1)}
                        className="w-8 h-8 bg-[#22242a] hover:bg-[#39ff14] hover:text-black text-white flex items-center justify-center transition-colors"
                      >
                        <Minus size={12} strokeWidth={3} />
                      </button>
                      <span data-testid={`qty-value-${i.cart_key}`} className="w-8 text-center text-sm tabular-nums text-white">{i.qty}</span>
                      <button
                        data-testid={`qty-plus-${i.cart_key}`}
                        onClick={() => setQty(i.cart_key, i.qty + 1)}
                        className="w-8 h-8 bg-[#22242a] hover:bg-[#39ff14] hover:text-black text-white flex items-center justify-center transition-colors"
                      >
                        <Plus size={12} strokeWidth={3} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`save-later-${i.cart_key}`}
                        onClick={() => saveForLater(i.cart_key)}
                        className="h-8 px-2 text-[#8e929b] hover:text-[#f5b700] flex items-center gap-1 transition-colors"
                        title="Save for later"
                      >
                        <Bookmark size={13} />
                        <span className="f-mono text-[9px] uppercase hidden sm:inline">Later</span>
                      </button>
                      {confirmRemove === i.cart_key ? (
                        <span className="flex items-center gap-1">
                          <button
                            data-testid={`remove-confirm-${i.cart_key}`}
                            onClick={() => { removeItem(i.cart_key); setConfirmRemove(null); }}
                            className="h-8 px-2 bg-red-500 text-white f-mono text-[9px] uppercase font-bold flex items-center gap-1"
                          >
                            <Check size={11} /> Remove?
                          </button>
                          <button
                            data-testid={`remove-cancel-${i.cart_key}`}
                            onClick={() => setConfirmRemove(null)}
                            className="h-8 px-2 bg-[#22242a] text-[#8e929b] f-mono text-[9px] uppercase"
                          >
                            Keep
                          </button>
                        </span>
                      ) : (
                        <button
                          data-testid={`remove-item-${i.cart_key}`}
                          onClick={() => setConfirmRemove(i.cart_key)}
                          className="w-8 h-8 text-[#8e929b] hover:text-red-500 flex items-center justify-center transition-colors"
                          title="Remove item"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {savedSection}
            </div>

            <div className="md:col-span-4">
              <div className="bg-[#121316] border border-[#22242a] p-6 space-y-4 sticky top-24">
                <h2 className="f-disp text-xl uppercase text-white border-b border-[#22242a] pb-4">Order Summary</h2>
                <div className="flex justify-between f-mono text-xs text-[#8e929b] uppercase tracking-wider">
                  <span>Items</span>
                  <span data-testid="cart-summary-count" className="text-white">{count}</span>
                </div>
                <div className="flex justify-between f-mono text-xs text-[#8e929b] uppercase tracking-wider">
                  <span>Shipping</span>
                  <span data-testid="cart-shipping" className="text-[#39ff14]">
                    {!hasPhysical
                      ? "Instant download · Free"
                      : shippingCents === 0
                      ? "USPS Priority · FREE"
                      : "USPS Priority · $9.99"}
                  </span>
                </div>
                {hasPhysical && (
                  <ProgressBar
                    testid="free-shipping-progress"
                    pct={shipPct}
                    color={shippingCents === 0 ? "bg-[#39ff14]" : "bg-[#39ff14]/60"}
                    label={shippingCents === 0
                      ? "Free shipping unlocked"
                      : `Add $${((10000 - afterDiscount) / 100).toFixed(2)} more → free shipping`}
                  />
                )}
                {hasPhysical && (
                  <p data-testid="delivery-estimate" className="f-mono text-[10px] text-[#8e929b] uppercase tracking-wider flex items-center gap-1.5">
                    <Truck size={11} className="text-[#39ff14]" /> Estimated delivery: <span className="text-white">{deliveryEstimate()}</span>
                  </p>
                )}
                {discountCents > 0 && (
                  <div data-testid="bulk-discount-row" className="flex justify-between f-mono text-xs uppercase tracking-wider">
                    <span className="text-[#f5b700]">Bulk discount (3+ items)</span>
                    <span className="text-[#f5b700]">−${(discountCents / 100).toFixed(2)}</span>
                  </div>
                )}
                {count > 0 && (
                  <ProgressBar
                    testid="bulk-discount-progress"
                    pct={bulkPct}
                    color={count >= 3 ? "bg-[#f5b700]" : "bg-[#f5b700]/60"}
                    label={count >= 3 ? "10% bulk discount applied" : `Add ${3 - count} more item${3 - count > 1 ? "s" : ""} → 10% off`}
                  />
                )}
                <div className="flex justify-between f-mono text-xs text-[#8e929b] uppercase tracking-wider">
                  <span>Tax</span>
                  <span data-testid="cart-tax-note">Calculated at checkout</span>
                </div>
                <div className="flex justify-between items-baseline border-t border-[#22242a] pt-4">
                  <span className="f-mono text-xs uppercase tracking-wider text-[#8e929b]">Total</span>
                  <span data-testid="cart-summary-total" className="f-disp text-3xl text-white tabular-nums">
                    ${(payableCents / 100).toFixed(2)}
                  </span>
                </div>
                <div data-testid="coupon-box" className="bg-[#0a0a0c] border border-[#22242a] p-3">
                  <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Tag size={11} className="text-[#39ff14]" /> Have a code?
                  </p>
                  {coupon ? (
                    <div className="flex items-center justify-between">
                      <span className="f-mono text-xs font-bold text-[#39ff14]">{coupon.code} · {coupon.percent_off}% off</span>
                      <button data-testid="coupon-remove" onClick={() => { setCoupon(null); setCouponInput(""); }}
                        className="f-mono text-[9px] uppercase text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        data-testid="coupon-input"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="CODE"
                        className="flex-1 bg-[#121316] border border-[#22242a] px-2.5 py-2 text-xs f-mono uppercase text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]"
                      />
                      <button data-testid="coupon-apply" onClick={applyCoupon} disabled={!couponInput.trim()}
                        className="f-mono text-[10px] uppercase px-3 py-2 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-50 transition-colors">
                        Apply
                      </button>
                    </div>
                  )}
                  {coupon && count >= 3 && (
                    <p className="f-mono text-[9px] text-[#f5b700] uppercase tracking-wider mt-2">Code replaces the 3+ bulk deal</p>
                  )}
                </div>
                <label data-testid="refund-policy-agree" className="flex items-start gap-3 cursor-pointer bg-[#0a0a0c] border border-[#22242a] p-3">
                  <input
                    type="checkbox"
                    data-testid="refund-policy-checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#39ff14]"
                  />
                  <span className="f-body text-[11px] text-[#8e929b] leading-relaxed">
                    I understand all sales are final — no refunds on templates or orders once placed.
                    Only exception: a delay caused by The Print Plug.{" "}
                    <Link to="/#policy" className="text-[#39ff14] underline">Read the refund policy</Link>
                  </span>
                </label>
                <button
                  onClick={checkout}
                  disabled={checkingOut || !agreed}
                  data-testid="checkout-button"
                  className="w-full py-4 bg-[#39ff14] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed text-black f-mono text-xs uppercase tracking-[0.14em] font-bold transition-colors d1-glow"
                >
                  {checkingOut ? "Starting Stripe…" : agreed ? "Checkout · Stripe" : "Agree to Policy to Checkout"}
                </button>
                <div data-testid="payment-security" className="bg-[#0a0a0c] border border-[#22242a] p-3 space-y-1.5">
                  <p className="f-mono text-[10px] text-[#39ff14] uppercase tracking-wider flex items-center gap-1.5">
                    <Check size={11} strokeWidth={3} /> 256-bit SSL · Payments by Stripe
                  </p>
                  <p className="f-body text-[10px] text-[#8e929b] leading-relaxed">
                    Cards, Apple Pay, Google Pay, Link, Cash App, Klarna and Affirm accepted. Your card details go straight to Stripe — we never see or store them. You're charged once, only when you confirm.
                  </p>
                  <p className="f-body text-[10px] text-[#8e929b] leading-relaxed">
                    Questions? <Link to="/track" className="text-[#39ff14] hover:text-white transition-colors">Track an order</Link>, see the <a href="/#shipping" className="text-[#39ff14] hover:text-white transition-colors">Shipping</a> or <a href="/#refund-policy" className="text-[#39ff14] hover:text-white transition-colors">Refund Policy</a>, or email <a href="mailto:sales@theprintplug.net" className="text-[#39ff14] hover:text-white transition-colors">sales@theprintplug.net</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
